/**
 * State of the clients list filters: reply status, funnel stage (KAN-616),
 * our Telegram account that talks to the client (KAN-618), client source
 * (KAN-682), "Отработанные" (KAN-734), client outcome (KAN-737), manager
 * (KAN-744), lead attribute (KAN-788) and the search string.
 * Any change resets pagination to the first page via `onChange`.
 *
 * KAN-623: the filters and the search string are remembered in localStorage
 * and restored when the page is opened again (the page number is not stored).
 * KAN-909: the manager filter is remembered separately (useManagerFilterChoice):
 * by default it is the user's own manager record, an explicit choice (also "Все")
 * is kept; a link preset wins until the user changes the filter.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  parseAttributeFilter,
  type CrmAttributeFilter,
  type FilterableAttribute,
} from '@/lib/crmAttributeFilter';
import { useManagerFilterChoice } from '@/hooks/useManagerFilterChoice';
import type { ManagerChoiceValues } from '@/lib/managerFilterChoice';
import { loadJson, removeJson, saveJson } from '@/lib/persistedState';
import { CLIENT_SOURCE_OPTIONS } from '@/types/client';
import type { ClientFilters } from '@/types/client';

export type ReplyFilterValue = 'all' | 'replied' | 'not_replied';

export const ALL_STAGES = 'all';

export const ALL_SENDERS = 'all';

/** KAN-682: "Все источники" option value (sends no `source` param). */
export const ALL_SOURCES = 'all';

/** KAN-737: "Все исходы" option value (sends no `outcome` param). */
export const ALL_OUTCOMES = 'all';

/** KAN-744: "Все" option of the manager filter (sends no `manager` param). */
export const ALL_MANAGERS = 'all';

/** KAN-744: "Без менеджера" option value (sent as manager=none). */
export const NO_MANAGER = 'none';

const CLIENT_MANAGER_VALUES: ManagerChoiceValues = { all: ALL_MANAGERS, mine: (id) => id };

export type OutcomeFilterValue = typeof ALL_OUTCOMES | 'open' | 'won' | 'lost';

const OUTCOME_VALUES: readonly OutcomeFilterValue[] = [ALL_OUTCOMES, 'open', 'won', 'lost'];

function isOutcome(value: unknown): value is OutcomeFilterValue {
  return typeof value === 'string' && (OUTCOME_VALUES as readonly string[]).includes(value);
}

export const CLIENT_FILTERS_STORAGE_KEY = 'smeta.clients.filters.v1';

interface StoredFilters {
  reply: ReplyFilterValue;
  stage: string;
  sender: string;
  source: string;
  worked: boolean;
  outcome: OutcomeFilterValue;
  /** KAN-788: lead attribute id; "" = no attribute filter. */
  attr: string;
  /** KAN-788: attribute value; "" = any filled value. */
  attrValue: string;
  search: string;
}

const DEFAULTS: StoredFilters = {
  reply: 'all',
  stage: ALL_STAGES,
  sender: ALL_SENDERS,
  source: ALL_SOURCES,
  worked: false,
  outcome: ALL_OUTCOMES,
  attr: '',
  attrValue: '',
  search: '',
};

const REPLY_VALUES: readonly ReplyFilterValue[] = ['all', 'replied', 'not_replied'];

function isReply(value: unknown): value is ReplyFilterValue {
  return typeof value === 'string' && (REPLY_VALUES as readonly string[]).includes(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/** KAN-682: a known source filter value ("all" or one of the catalogue codes). */
function isSource(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    (value === ALL_SOURCES || CLIENT_SOURCE_OPTIONS.some((o) => o.value === value))
  );
}

/** Reads the stored filters; each invalid field falls back to its default. */
function loadStoredFilters(): StoredFilters {
  const raw = loadJson(CLIENT_FILTERS_STORAGE_KEY);
  if (!raw || typeof raw !== 'object') return DEFAULTS;
  const data = raw as Record<string, unknown>;
  return {
    reply: isReply(data.reply) ? data.reply : DEFAULTS.reply,
    stage: nonEmptyString(data.stage) ? data.stage : DEFAULTS.stage,
    sender: nonEmptyString(data.sender) ? data.sender : DEFAULTS.sender,
    source: isSource(data.source) ? data.source : DEFAULTS.source,
    worked: data.worked === true,
    outcome: isOutcome(data.outcome) ? data.outcome : DEFAULTS.outcome,
    attr: nonEmptyString(data.attr) ? data.attr : DEFAULTS.attr,
    attrValue:
      nonEmptyString(data.attr) && typeof data.attrValue === 'string'
        ? data.attrValue
        : DEFAULTS.attrValue,
    search: typeof data.search === 'string' ? data.search : DEFAULTS.search,
  };
}

/**
 * Filter preset coming from a link (dashboard funnel card: `?funnel=&stage=`;
 * KAN-682: `?source=`; KAN-734: `?worked=1`; KAN-737: `&outcome=open|won|lost`
 * next to the stage; KAN-744: `?manager=<id>|none` from the manager card). When given, it wins over the remembered filters and
 * clears the other ones, so the list shows exactly the clients counted on the
 * link source.
 */
export interface StagePreset {
  stage?: string | null;
  funnel?: string | null;
  source?: string | null;
  worked?: boolean;
  outcome?: string | null;
  manager?: string | null;
}

function hasPreset(preset?: StagePreset | null): preset is StagePreset {
  return !!preset && !!(preset.stage || isSource(preset.source) || preset.worked || preset.manager);
}

/** KAN-909: manager forced by a link preset ("Все" when the link has none); null - no preset. */
function presetManager(preset?: StagePreset | null): string | null {
  return hasPreset(preset) ? preset.manager || ALL_MANAGERS : null;
}

function initialFilters(preset?: StagePreset | null): StoredFilters {
  if (hasPreset(preset)) {
    const result = { ...DEFAULTS };
    if (preset.worked) result.worked = true;
    if (preset.stage) {
      result.stage = preset.funnel ? `${preset.funnel}:${preset.stage}` : preset.stage;
    }
    if (isSource(preset.source)) result.source = preset.source;
    if (isOutcome(preset.outcome)) result.outcome = preset.outcome;
    return result;
  }
  return loadStoredFilters();
}

/**
 * `attributes` (KAN-788): lead attributes of the organization; undefined while
 * loading or after a failed load. Once loaded, a remembered attribute filter
 * that is no longer valid (deleted attribute, removed choice option) is
 * dropped: the backend answers such a request with 400.
 */
export function useClientListFilters(
  onChange: () => void,
  preset?: StagePreset | null,
  attributes?: readonly FilterableAttribute[]
) {
  const [initial] = useState(() => initialFilters(preset));
  const [reply, setReplyState] = useState<ReplyFilterValue>(initial.reply);
  const [stage, setStageState] = useState<string>(initial.stage);
  const [sender, setSenderState] = useState<string>(initial.sender);
  const [source, setSourceState] = useState<string>(initial.source);
  const [worked, setWorkedState] = useState<boolean>(initial.worked);
  const [outcome, setOutcomeState] = useState<OutcomeFilterValue>(initial.outcome);
  const [managerOverride, setManagerOverride] = useState<string | null>(() => presetManager(preset));
  const managerChoice = useManagerFilterChoice('clients', CLIENT_MANAGER_VALUES, managerOverride);
  const manager = managerChoice.value;
  const { choose: chooseManager, clear: clearManager } = managerChoice;
  const [attr, setAttrState] = useState<string>(initial.attr);
  const [attrValue, setAttrValueState] = useState<string>(initial.attrValue);
  const [search, setSearchState] = useState<string>(initial.search);

  useEffect(() => {
    if (!attributes || !attr) return;
    if (parseAttributeFilter(attr, attrValue, attributes)) return;
    setAttrState(DEFAULTS.attr);
    setAttrValueState(DEFAULTS.attrValue);
    onChange();
  }, [attributes, attr, attrValue, onChange]);

  useEffect(() => {
    const isDefault =
      reply === DEFAULTS.reply &&
      stage === DEFAULTS.stage &&
      sender === DEFAULTS.sender &&
      source === DEFAULTS.source &&
      worked === DEFAULTS.worked &&
      outcome === DEFAULTS.outcome &&
      attr === DEFAULTS.attr &&
      search === DEFAULTS.search;
    if (isDefault) removeJson(CLIENT_FILTERS_STORAGE_KEY);
    else
      saveJson(CLIENT_FILTERS_STORAGE_KEY, {
        reply,
        stage,
        sender,
        source,
        worked,
        outcome,
        attr,
        attrValue,
        search,
      });
  }, [reply, stage, sender, source, worked, outcome, attr, attrValue, search]);

  const setReply = useCallback(
    (value: ReplyFilterValue) => {
      setReplyState(value);
      onChange();
    },
    [onChange]
  );

  const setStage = useCallback(
    (value: string) => {
      setStageState(value);
      onChange();
    },
    [onChange]
  );

  const setSender = useCallback(
    (value: string) => {
      setSenderState(value);
      onChange();
    },
    [onChange]
  );

  const setSource = useCallback(
    (value: string) => {
      setSourceState(value);
      onChange();
    },
    [onChange]
  );

  const setWorked = useCallback(
    (value: boolean) => {
      setWorkedState(value);
      onChange();
    },
    [onChange]
  );

  const setOutcome = useCallback(
    (value: OutcomeFilterValue) => {
      setOutcomeState(value);
      onChange();
    },
    [onChange]
  );

  const setManager = useCallback(
    (value: string) => {
      setManagerOverride(null);
      chooseManager(value);
      onChange();
    },
    [chooseManager, onChange]
  );

  /** KAN-909: the chosen manager was deleted: forget the choice (back to the default). */
  const dropManager = useCallback(() => {
    setManagerOverride(null);
    clearManager();
    onChange();
  }, [clearManager, onChange]);

  const attributeFilter = useMemo<CrmAttributeFilter | null>(
    () => (attr ? { attr, value: attrValue || null } : null),
    [attr, attrValue]
  );

  /** KAN-788: null clears the attribute filter; value null = any filled. */
  const setAttributeFilter = useCallback(
    (value: CrmAttributeFilter | null) => {
      setAttrState(value?.attr ?? DEFAULTS.attr);
      setAttrValueState(value?.value ?? DEFAULTS.attrValue);
      onChange();
    },
    [onChange]
  );

  const setSearch = useCallback(
    (value: string) => {
      setSearchState(value);
      onChange();
    },
    [onChange]
  );

  /** Clears all filters, the search string and the remembered state. */
  const reset = useCallback(() => {
    setReplyState(DEFAULTS.reply);
    setStageState(DEFAULTS.stage);
    setSenderState(DEFAULTS.sender);
    setSourceState(DEFAULTS.source);
    setWorkedState(DEFAULTS.worked);
    setOutcomeState(DEFAULTS.outcome);
    setManagerOverride(null);
    clearManager();
    setAttrState(DEFAULTS.attr);
    setAttrValueState(DEFAULTS.attrValue);
    setSearchState(DEFAULTS.search);
    removeJson(CLIENT_FILTERS_STORAGE_KEY);
    onChange();
  }, [clearManager, onChange]);

  const isActive =
    reply !== 'all' ||
    stage !== ALL_STAGES ||
    sender !== ALL_SENDERS ||
    source !== ALL_SOURCES ||
    outcome !== ALL_OUTCOMES ||
    manager !== managerChoice.defaultValue ||
    attr !== DEFAULTS.attr ||
    worked;

  /**
   * KAN-788: the attribute filter that may go to the backend. null until the
   * attributes are loaded (or when they are unavailable) and for a remembered
   * filter that is not valid for this organization: the backend answers such
   * a request with 400, so the list is requested without it.
   */
  const requestAttributeFilter = useMemo<CrmAttributeFilter | null>(
    () => (attributes && attr ? parseAttributeFilter(attr, attrValue, attributes) : null),
    [attributes, attr, attrValue]
  );

  /** Adds the active filters to the request params (absent key = "all"). */
  const applyTo = useCallback(
    (params: ClientFilters): ClientFilters => {
      const result = { ...params };
      if (reply !== 'all') result.replied = reply === 'replied';
      if (stage !== ALL_STAGES) {
        // Option value is `${funnel_codename}:${codename}` (codename repeats across funnels).
        const sep = stage.indexOf(':');
        result.stage = sep >= 0 ? stage.slice(sep + 1) : stage;
        if (sep > 0) result.funnel = stage.slice(0, sep);
      }
      if (sender !== ALL_SENDERS) result.sender_account = sender;
      if (source !== ALL_SOURCES) result.source = source;
      if (worked) result.worked = 1;
      if (outcome !== ALL_OUTCOMES) result.outcome = outcome;
      if (manager !== ALL_MANAGERS) result.manager = manager;
      if (requestAttributeFilter) {
        result.attr = requestAttributeFilter.attr;
        if (requestAttributeFilter.value) result.attr_value = requestAttributeFilter.value;
      }
      return result;
    },
    [reply, stage, sender, source, worked, outcome, manager, requestAttributeFilter]
  );

  return {
    reply,
    stage,
    sender,
    source,
    worked,
    outcome,
    manager,
    attr,
    attrValue,
    attributeFilter,
    search,
    setReply,
    setStage,
    setSender,
    setSource,
    setWorked,
    setOutcome,
    setManager,
    dropManager,
    setAttributeFilter,
    setSearch,
    reset,
    isActive,
    applyTo,
  };
}
